import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchTenantAudit, queryKeys, type TenantAuditRow } from '../../lib/api';

// Exemplar: docs/design-system/exemplars -- cursor-paginated activity log,
// closest existing surface is the onboarding events timeline
// (onboarding-events.tsx) -- timestamp/actor/action rows with a "Load more"
// footer rather than numbered pages.

function renderDetail(detail: unknown): React.ReactNode {
  if (detail == null) return <span className="text-muted">—</span>;
  if (typeof detail !== 'object' || Array.isArray(detail)) {
    return <span className="font-mono text-caption">{String(detail)}</span>;
  }
  const entries = Object.entries(detail as Record<string, unknown>);
  if (entries.length === 0) return <span className="text-muted">—</span>;
  return (
    <dl className="flex flex-col gap-0.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-1.5 text-caption">
          <dt className="text-muted">{k}:</dt>
          <dd className="text-secondary min-w-0 truncate m-0">
            {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AuditTab({ tenantId }: { tenantId: string }) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const cursor = cursors[cursors.length - 1];

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.tenantAudit(tenantId, cursor),
    queryFn: () => fetchTenantAudit(tenantId, cursor !== undefined ? { cursor } : {}),
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-[85%]" />
        <Skeleton className="h-10 w-[65%]" />
      </div>
    );
  }
  if (isError) {
    return <ErrorState title="Could not load the audit log" onRetry={() => void refetch()} />;
  }

  const rows: TenantAuditRow[] = data?.rows ?? [];

  if (rows.length === 0 && cursors.length === 1) {
    return (
      <EmptyState
        title="No audit history yet"
        description="Admin-lane changes to this tenant will appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-control border border-border">
        <Table dense>
          <TableHead>
            <tr>
              <TableHeaderCell>Timestamp</TableHeaderCell>
              <TableHeaderCell>Actor</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
              <TableHeaderCell>Detail</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-surface-elevated transition-colors">
                <TableCell className="tabular-nums text-caption text-secondary">
                  {new Date(row.at).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-caption">{row.actorUid}</TableCell>
                <TableCell className="text-body-sm text-primary">{row.action}</TableCell>
                <TableCell>{renderDetail(row.detail)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {/* Fix round 2 (I7): each cursor is a distinct query key, so `rows` is
          only ever the CURRENT page -- "Load more" implied accumulation
          that never happened, and there was no way back. Relabelled to
          Next/Previous (same idiom as module-matrix.tsx's offset pager) --
          `cursors` already held every prior page's cursor, so Previous is
          just popping the stack back to it. */}
      {(cursors.length > 1 || data?.nextCursor) && (
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            disabled={cursors.length === 1}
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={!data?.nextCursor}
            onClick={() => setCursors((prev) => [...prev, data.nextCursor!])}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
